import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import {
  generateContractSummary,
  generateEpkFamilyPerformance,
  generateUserSummary,
  userArticlesMock,
} from "src/app/services/mocks/dashboard-mock";
import { generateMockContracts } from "../v2/mocks/contracts/generateMockContracts";
import { generateMockContractsFromExternal } from "../v2/mocks/contracts/generateMockContractsFromExternal";
import { generateMockContractsFromNav } from "../v2/mocks/contracts/generateMockContractsFromNav";
import { generateMockActions } from "../v2/mocks/contracts/generateMockActions";
import { generateMockConsents } from "../v2/mocks/consents/generateMockConsents";
import { generateMockUserBenefits } from "../v2/mocks/benefits/generateMockUserBenefits";
import { generateMockUserSettings } from "../v2/mocks/contracts/generateMockUserSettings";
import { generateMockIncome } from "../v2/mocks/income/generateMockIncome";
import { generateMockSmartAccountStatus } from "../v2/mocks/smart-account/generateMockSmartAccountStatus";
import { ConsentModel } from "../v2/models/consent.model";
import { ConsentsDto, ConsentsInput, IncomeInput, IncomeRepresentation } from "../api/smart-pensjon-rest-api/models";
import { readMockScenario, MockScenario } from "../v2/mocks/mock-scenario";
import { readAiMockOverride } from "../components/dev/ai-mock-chat/ai-mock-chat-storage";

type MockConsents = ConsentModel & ConsentsDto;
type ConsentKey = Exclude<keyof ConsentsInput, "purposeText">;

const CONSENT_KEYS: readonly ConsentKey[] = [
  "hasNavConsent",
  "hasOfaConsent",
  "hasCustomerSuppliedDataConsent",
  "hasNorskPensjonConsent",
  "hasGroupConsent",
  "hasElectronicSolicitationConsent",
  "hasSocialMediaSolicitationConsent",
];

/**
 * HTTP Interceptor that provides offline/mock data for development and testing.
 * When offlineMode is enabled in environment config, this interceptor intercepts
 * HTTP requests and returns mock data instead of making real network calls.
 *
 * To add support for new endpoints:
 * 1. Create mock data in src/app/services/mocks/
 * 2. Import the mock data in this file
 * 3. Add a new case in the switch statement below
 */
@Injectable()
export class OfflineDataInterceptor implements HttpInterceptor {
  private consentOverride: MockConsents | null = null;
  private incomeOverride: IncomeRepresentation | null = null;
  /** The last salary the user sent. Kept apart, so switching back to USER without a salary restores it. */
  private enteredSalary: number | null = null;

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only intercept when offline mode is enabled
    if (!environment.offlineMode) {
      return next.handle(req);
    }

    // Extract the endpoint path from the URL
    const url = req.url;
    const urlPath = this.extractPath(url);

    // The AI mock chat override (set by the floating AI mock chat widget) takes priority over the regular
    // mock-scenario dev-row when both are set.
    const scenario = readAiMockOverride() ?? readMockScenario();

    // The onboarding saves send PATCH, the v2 ConsentService still sends POST.
    if ((req.method === "PATCH" || req.method === "POST") && urlPath.endsWith("/user/consents")) {
      return this.handleConsentUpdate(req, scenario);
    }

    if (req.method === "PUT" && urlPath.endsWith("/user/income")) {
      return this.handleIncomeUpdate(req, scenario);
    }

    // Match URL to mock data.
    const mockData = this.getMockData(urlPath, scenario);

    if (mockData !== null) {
      // Return mock data as HTTP response
      return of(
        new HttpResponse({
          status: 200,
          body: mockData,
        }),
      );
    }

    // If no mock data found, pass through to real backend
    return next.handle(req);
  }

  /**
   * Merges the sent consents into the current mock consents, as the backend's PATCH does: a consent that is left
   * out or null stays as it was. The POST sends all four of its consents, so the merge works for it too.
   */
  private handleConsentUpdate(req: HttpRequest<unknown>, scenario: MockScenario): Observable<HttpEvent<unknown>> {
    const input = req.body as ConsentsInput;
    this.consentOverride = CONSENT_KEYS.reduce<MockConsents>((consents, consent) => {
      const value = input[consent];
      if (value === null || value === undefined) {
        return consents;
      }
      return { ...consents, [consent]: value };
    }, this.currentConsents(scenario));
    return of(new HttpResponse({ status: 200, body: { payload: this.consentOverride } }));
  }

  /**
   * Like the backend, answers 409 without the storage consent. Otherwise a sent salary makes the source USER,
   * and the EMPLOYER source takes the salary from the employer. The USER source shows the last salary the user
   * sent, also after a switch to EMPLOYER and back.
   */
  private handleIncomeUpdate(req: HttpRequest<unknown>, scenario: MockScenario): Observable<HttpEvent<unknown>> {
    if (this.currentConsents(scenario).hasCustomerSuppliedDataConsent !== true) {
      return throwError(() => new HttpErrorResponse({ status: 409, statusText: "Conflict", url: req.url }));
    }

    const input = req.body as IncomeInput;
    const current = this.incomeOverride ?? generateMockIncome(scenario).payload;
    const sentSalary = input.annualSalary ?? null;
    if (sentSalary !== null) {
      this.enteredSalary = sentSalary;
    }
    const source = input.incomeSource ?? (sentSalary !== null ? "USER" : current.source);
    this.incomeOverride = {
      ...current,
      source,
      annualSalary: source === "EMPLOYER" ? current.employerSalary : (this.enteredSalary ?? current.annualSalary),
    };
    return of(new HttpResponse({ status: 200, body: { payload: this.incomeOverride } }));
  }

  private currentConsents(scenario: MockScenario): MockConsents {
    return this.consentOverride ?? generateMockConsents(scenario).payload;
  }

  /**
   * Extract the path from a URL, removing the base URL and query parameters
   */
  private extractPath(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      // If URL parsing fails, try to extract path manually
      const pathMatch = url.match(/\/[^?]*/);
      return pathMatch ? pathMatch[0] : url;
    }
  }

  /**
   * Map URL paths to mock data
   * Add new cases here to support additional endpoints
   */
  private getMockData(urlPath: string, scenario: MockScenario): unknown | null {
    // Dashboard endpoints
    if (urlPath.endsWith("/min-pensjon/user/summary")) {
      return generateUserSummary(scenario);
    }

    if (urlPath.endsWith("/min-pensjon/contracts/summary")) {
      return generateContractSummary(scenario);
    }

    if (urlPath.endsWith("/min-pensjon/contracts/epk-family/performance")) {
      return generateEpkFamilyPerformance(scenario);
    }

    if (urlPath.endsWith("/min-pensjon/user/articles")) {
      return userArticlesMock;
    }

    if (urlPath.endsWith("/min-pensjon/contracts")) {
      return generateMockContracts(scenario);
    }

    if (urlPath.endsWith("/min-pensjon/contracts/external")) {
      return generateMockContractsFromExternal(scenario);
    }

    if (urlPath.endsWith("/min-pensjon/contracts/nav")) {
      return generateMockContractsFromNav(scenario);
    }

    if (urlPath.endsWith("/min-pensjon/contracts/actions")) {
      return generateMockActions();
    }

    if (urlPath.endsWith("/user/benefits")) {
      return generateMockUserBenefits();
    }

    if (urlPath.endsWith("/user/consents")) {
      return this.consentOverride !== null ? { payload: this.consentOverride } : generateMockConsents(scenario);
    }

    if (urlPath.endsWith("/user/settings")) {
      return { payload: generateMockUserSettings(scenario) };
    }

    if (urlPath.endsWith("/user/income")) {
      return this.incomeOverride !== null ? { payload: this.incomeOverride } : generateMockIncome(scenario);
    }

    if (urlPath.endsWith("/bank/smart-account")) {
      return generateMockSmartAccountStatus();
    }

    // No mock data found for this URL
    return null;
  }
}
